import { useAuth } from "react-oidc-context";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import {
  digiLockerResult,
  digiLockerWaiting,
  getKyc,
  startDigiLocker,
  uploadAadhaar,
  uploadPreferred,
  BUSINESS_TYPES,
  activateApplicant,
  personalBusiness,
  verifyBank,
  verifyCin,
  verifyGstin,
  verifyPan,
  verifyOwnerPan,
  type BusinessType,
  type KycCheck,
  type KycState,
} from "../../api/applicantOnboarding";

function Outcome({ check, passed }: { check: KycCheck; passed: string }) {
  if (check.status === "PASSED") {
    return (
      <Alert severity="success" sx={{ mt: 1 }}>
        {passed}
      </Alert>
    );
  }
  if (check.status === "REVIEW") {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        {check.reason}
      </Alert>
    );
  }
  if (check.status === "FAILED" && check.reason) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        {check.reason} {check.attemptsleft > 0 && `${check.attemptsleft} attempt${check.attemptsleft === 1 ? "" : "s"} left.`}
      </Alert>
    );
  }
  return null;
}

/** Online verification (owner 2026-10-30): free; PAN, then Aadhaar, then bank; GSTIN if they have one. */
export function KycSteps() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const qc = useQueryClient();
  const key = ["applicant", "kyc", ns];
  const q = useQuery({
    queryKey: key,
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: async () => (await getKyc(identity, ns)).data,
  });
  const [type, setType] = useState<BusinessType | "">("");
  const [pan, setPan] = useState("");
  const [ownerPan, setOwnerPan] = useState("");
  const [account, setAccount] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [gstin, setGstin] = useState("");
  const [cin, setCin] = useState("");
  const [error, setError] = useState("");

  const run = useMutation({
    mutationFn: (action: () => Promise<KycState>) => action(),
    onMutate: () => setError(""),
    onSuccess: (state) => {
      qc.setQueryData(key, state);
      // The whole page leaves for DigiLocker and comes back to Welcome.
      if (state.url) window.location.assign(state.url);
    },
    onError: (e: Error) => setError(e.message),
  });

  // The login's role and namespace change, so a fresh sign-in opens the dashboard.
  const auth = useAuth();
  const activate = useMutation({
    mutationFn: () => activateApplicant(identity, ns),
    onMutate: () => setError(""),
    onSuccess: () => void auth.signoutRedirect(),
    onError: (e: Error) => setError(e.message),
  });

  // Coming back from DigiLocker: read how it went, once.
  const read = useRef(false);
  const data = q.data;
  useEffect(() => {
    if (!data || read.current || !digiLockerWaiting(data.aadhaar)) return;
    read.current = true;
    run.mutate(async () => (await digiLockerResult(identity, ns)).data);
    // Runs when the state first arrives; identity is a new object on every render.
  }, [data]);

  if (q.isError) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        {(q.error as Error).message}
      </Alert>
    );
  }
  if (!data) return null;
  if (!data.available) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Online verification opens here soon.
      </Typography>
    );
  }

  const busy = run.isPending;
  const chosenType = type || data.businesstype || "";
  const panDone = data.pan.status === "PASSED";
  const aadhaarDone = data.aadhaar.status === "PASSED";
  const isCompany = !personalBusiness(data.businesstype);
  const bankNameDiffers = data.bank.method === "name_differs";
  const bankDone = data.bank.status === "PASSED";
  const ownerDone = !isCompany || data.ownerpan.status === "PASSED";
  const aadhaarOpen = panDone && ownerDone && !aadhaarDone && data.aadhaar.status !== "REVIEW";
  const waiting = digiLockerWaiting(data.aadhaar);
  const showDigiLocker = aadhaarOpen && !waiting && Boolean(data.aadhaar.digilockerleft) && !uploadPreferred(data.aadhaar);
  const upload = (
    <Button component="label" variant={uploadPreferred(data.aadhaar) ? "contained" : "outlined"} disabled={busy} data-testid="aadhaar-upload">
      Upload Aadhaar photo
      <input hidden type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={(e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (f) run.mutate(() => uploadAadhaar(identity, ns, f));
      }} />
    </Button>
  );

  return (
    <Box sx={{ mt: 1 }} data-testid="kyc-steps">

      <Typography variant="body2" color="text.secondary" data-testid="kyc-step-list">
        {personalBusiness(chosenType)
          ? "In order: your PAN, your bank account, GSTIN if you have one, then your Aadhaar."
          : "In order: business PAN, business bank account, GSTIN if you have one, owner's PAN, then owner's Aadhaar."}
      </Typography>

      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        {isCompany ? "Business PAN" : "PAN"} {panDone && "✓"}
      </Typography>
      {!panDone && data.pan.status !== "REVIEW" && (
        <>
          {!data.businesstype && (
            <RadioGroup row value={chosenType} onChange={(e) => setType(e.target.value as BusinessType)}>
              {BUSINESS_TYPES.map((b) => (
                <FormControlLabel key={b.value} value={b.value} control={<Radio size="small" />} label={b.label} />
              ))}
            </RadioGroup>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <TextField size="small" label={personalBusiness(chosenType) ? "Your PAN" : "Business PAN"} value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())}
              slotProps={{ htmlInput: { maxLength: 10, "data-testid": "kyc-pan" } }} />
            <Button variant="contained" disabled={busy || !chosenType || pan.length !== 10} data-testid="kyc-pan-verify"
              onClick={() => run.mutate(async () => (await verifyPan(identity, ns, chosenType as BusinessType, pan)).data)}>
              Verify PAN
            </Button>
          </Stack>
        </>
      )}
      {panDone && (
        <Typography variant="body2" sx={{ mt: 1 }} data-testid="kyc-business-type">
          Business type: {BUSINESS_TYPES.find((b) => b.value === data.businesstype)?.label ?? "Company"}
        </Typography>
      )}
      <Outcome check={data.pan} passed={`PAN verified${data.pan.name ? `. Legal name: ${data.pan.name}` : ""}.`} />

      {panDone && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            {isCompany ? "Business bank account" : "Bank account"} {data.bank.status === "PASSED" && "✓"}
          </Typography>
          {data.bank.status !== "PASSED" && (data.bank.status !== "REVIEW" || bankNameDiffers) && (
            <>
              <Typography variant="body2" color="text.secondary">
                We deposit ₹1 in this account to confirm it.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField size="small" label="Account number" value={account} onChange={(e) => setAccount(e.target.value)} slotProps={{ htmlInput: { "data-testid": "kyc-account" } }} />
                <TextField size="small" label="IFSC" value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} slotProps={{ htmlInput: { maxLength: 11, "data-testid": "kyc-ifsc" } }} />
                <Button variant="contained" disabled={busy || !account || ifsc.length !== 11} data-testid="kyc-bank-verify"
                  onClick={() => run.mutate(async () => (await verifyBank(identity, ns, account, ifsc)).data)}>
                  Verify account
                </Button>
              </Stack>
              {bankNameDiffers && data.bank.name && (
                <Button variant="outlined" sx={{ mt: 1 }} disabled={busy || !account || ifsc.length !== 11} data-testid="kyc-bank-confirm"
                  onClick={() => run.mutate(async () => (await verifyBank(identity, ns, account, ifsc, data.bank.name)).data)}>
                  Yes, {data.bank.name} is my account
                </Button>
              )}
            </>
          )}
          <Outcome check={data.bank} passed={`Bank account verified${data.bank.name ? `: ${data.bank.name}` : ""}.`} />
        </>
      )}

      {panDone && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            GSTIN (if you have one) {data.gstin.status === "PASSED" && "✓"}
          </Typography>
          {data.gstin.status !== "PASSED" && data.gstin.attemptsleft > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField size="small" label="GSTIN" value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} slotProps={{ htmlInput: { maxLength: 15, "data-testid": "kyc-gstin" } }} />
              <Button variant="outlined" disabled={busy || gstin.length !== 15} data-testid="kyc-gstin-verify"
                onClick={() => run.mutate(async () => (await verifyGstin(identity, ns, gstin)).data)}>
                Verify GSTIN
              </Button>
            </Stack>
          )}
          <Outcome check={data.gstin} passed="GSTIN verified." />
        </>
      )}

      {panDone && (data.businesstype === "company" || data.businesstype === "llp") && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            {data.businesstype === "llp" ? "LLPIN" : "CIN"} (optional) {data.cin?.status === "PASSED" && "✓"}
          </Typography>
          {data.cin?.status === "TYPED" ? (
            <Typography variant="body2" color="text.secondary">LLPIN saved. It is not verified online.</Typography>
          ) : data.cin?.status !== "PASSED" && (data.cin?.attemptsleft ?? 1) > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField size="small" label={data.businesstype === "llp" ? "LLPIN" : "CIN"} value={cin} onChange={(e) => setCin(e.target.value.toUpperCase())} slotProps={{ htmlInput: { maxLength: 21, "data-testid": "kyc-cin" } }} />
              <Button variant="outlined" disabled={busy || !cin.trim()} data-testid="kyc-cin-verify"
                onClick={() => run.mutate(async () => (await verifyCin(identity, ns, cin)).data)}>
                {data.businesstype === "llp" ? "Save LLPIN" : "Verify CIN"}
              </Button>
            </Stack>
          )}
          {data.cin && data.cin.status !== "TYPED" && <Outcome check={data.cin} passed="CIN verified." />}
        </>
      )}

      {panDone && bankDone && isCompany && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Owner's PAN {data.ownerpan.status === "PASSED" && "✓"}
          </Typography>
          {data.ownerpan.status !== "PASSED" && data.ownerpan.status !== "REVIEW" && (
            <>
              <Typography variant="body2" color="text.secondary">
                The owner's personal PAN. The name on it becomes the owner name. We keep only the name and the last four characters.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField size="small" label="Owner's PAN" value={ownerPan} onChange={(e) => setOwnerPan(e.target.value.toUpperCase())}
                  slotProps={{ htmlInput: { maxLength: 10, "data-testid": "kyc-owner-pan" } }} />
                <Button variant="contained" disabled={busy || ownerPan.length !== 10} data-testid="kyc-owner-pan-verify"
                  onClick={() => run.mutate(async () => (await verifyOwnerPan(identity, ns, ownerPan)).data)}>
                  Verify owner's PAN
                </Button>
              </Stack>
            </>
          )}
          <Outcome check={data.ownerpan} passed={`Owner's PAN verified${data.ownerpan.name ? `. Owner name: ${data.ownerpan.name}` : ""}.`} />
        </>
      )}

      {panDone && bankDone && ownerDone && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            {isCompany ? "Owner's Aadhaar" : "Aadhaar"} {aadhaarDone && "✓"}
          </Typography>
          {isCompany && !aadhaarDone && (
            <Typography variant="body2" color="text.secondary">
              The owner's Aadhaar. Its name must match the owner's PAN. We keep only the name and the last four digits.
            </Typography>
          )}
          {waiting && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button variant="outlined" disabled={busy} onClick={() => run.mutate(async () => (await digiLockerResult(identity, ns)).data)}>
                Check DigiLocker result
              </Button>
              {upload}
            </Stack>
          )}
          {aadhaarOpen && !waiting && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {showDigiLocker && (
                <Button variant="contained" disabled={busy} data-testid="kyc-digilocker"
                  onClick={() => run.mutate(async () => (await startDigiLocker(identity, ns)).data)}>
                  Verify with DigiLocker
                </Button>
              )}
              {upload}
            </Stack>
          )}
          <Outcome check={data.aadhaar} passed={`${isCompany ? "Owner's Aadhaar" : "Aadhaar"} verified${data.aadhaar.last4 ? ` (ending ${data.aadhaar.last4})` : ""}.${data.aadhaar.address ? ` Address: ${data.aadhaar.address}.` : ""}`} />
        </>
      )}

      {/* Below the steps: later steps are hidden, so this is beside the one in use. */}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }} data-testid="kyc-error">
          {error}
        </Alert>
      )}

      {data.complete && (
        <Alert severity="success" sx={{ mt: 2 }} data-testid="kyc-complete">
          All checks passed.
          <Box sx={{ mt: 1 }}>
            <Button variant="contained" disabled={activate.isPending} onClick={() => activate.mutate()} data-testid="kyc-activate">
              Finish and open my dashboard
            </Button>
          </Box>
        </Alert>
      )}
    </Box>
  );
}
